import { createPerspectiveTransform } from "./perspective-homography";

/** Normalized image point in [0, 1] relative to natural width/height. */
export type NormPoint = { x: number; y: number };
export type PixelPoint = { x: number; y: number };

export type ImageContentBox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type QuadHomography = {
  sampleSource(destX: number, destY: number): PixelPoint;
};

export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function distance(a: PixelPoint, b: PixelPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Content box of an <img> that is sized with max-width/max-height (no object-fit
 * letterboxing). Falls back to object-fit:contain math only when the element
 * box aspect differs from the natural aspect (legacy / mis-styled layouts).
 */
export function getImageContentBox(
  img: Pick<HTMLImageElement, "getBoundingClientRect" | "naturalWidth" | "naturalHeight">,
): ImageContentBox {
  const rect = img.getBoundingClientRect();
  const naturalWidth = img.naturalWidth || rect.width;
  const naturalHeight = img.naturalHeight || rect.height;
  if (rect.width <= 0 || rect.height <= 0) {
    return { left: rect.left, top: rect.top, width: 0, height: 0 };
  }

  const imageRatio = naturalWidth / naturalHeight;
  const boxRatio = rect.width / rect.height;
  if (
    !Number.isFinite(imageRatio) ||
    !Number.isFinite(boxRatio) ||
    Math.abs(imageRatio - boxRatio) < 0.002
  ) {
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }

  // object-fit: contain + object-position: center
  if (boxRatio > imageRatio) {
    const width = rect.height * imageRatio;
    return {
      left: rect.left + (rect.width - width) / 2,
      top: rect.top,
      width,
      height: rect.height,
    };
  }

  const height = rect.width / imageRatio;
  return {
    left: rect.left,
    top: rect.top + (rect.height - height) / 2,
    width: rect.width,
    height,
  };
}

/** Map viewport client coords → normalized natural-image coords. */
export function clientToNormalized(
  clientX: number,
  clientY: number,
  box: ImageContentBox,
): NormPoint | null {
  if (box.width <= 0 || box.height <= 0) return null;
  return {
    x: clamp01((clientX - box.left) / box.width),
    y: clamp01((clientY - box.top) / box.height),
  };
}

/** Map normalized coords → position inside a stage-local content box. */
export function normalizedToStageLocal(
  point: NormPoint,
  box: ImageContentBox,
  stageLeft: number,
  stageTop: number,
): PixelPoint {
  return {
    x: box.left - stageLeft + point.x * box.width,
    y: box.top - stageTop + point.y * box.height,
  };
}

/**
 * Order corners as TL, TR, BR, BL (screen y-down).
 * Works for convex quads; bowties are not supported.
 */
export function orderCorners(
  corners: [NormPoint, NormPoint, NormPoint, NormPoint],
): [NormPoint, NormPoint, NormPoint, NormPoint] {
  const center = corners.reduce(
    (acc, point) => ({ x: acc.x + point.x / 4, y: acc.y + point.y / 4 }),
    { x: 0, y: 0 },
  );
  const sorted = [...corners].sort(
    (a, b) =>
      Math.atan2(a.y - center.y, a.x - center.x) -
      Math.atan2(b.y - center.y, b.x - center.x),
  );
  const topLeftIndex = sorted.reduce((best, point, index) => {
    const bestPoint = sorted[best];
    return point.x + point.y < bestPoint.x + bestPoint.y ? index : best;
  }, 0);
  const rotated = [
    ...sorted.slice(topLeftIndex),
    ...sorted.slice(0, topLeftIndex),
  ] as [NormPoint, NormPoint, NormPoint, NormPoint];
  // Prefer TL,TR,BR,BL over TL,BL,BR,TR
  return rotated[1].x < rotated[3].x
    ? [rotated[0], rotated[3], rotated[2], rotated[1]]
    : rotated;
}

export function normalizedToPixels(
  corners: [NormPoint, NormPoint, NormPoint, NormPoint],
  naturalWidth: number,
  naturalHeight: number,
): [PixelPoint, PixelPoint, PixelPoint, PixelPoint] {
  const maxX = Math.max(1, naturalWidth - 1);
  const maxY = Math.max(1, naturalHeight - 1);
  return orderCorners(corners).map((point) => ({
    x: clamp01(point.x) * maxX,
    y: clamp01(point.y) * maxY,
  })) as [PixelPoint, PixelPoint, PixelPoint, PixelPoint];
}

export function outputSizeForQuad(
  corners: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  maxEdge = 3200,
): { outW: number; outH: number } {
  const [tl, tr, br, bl] = corners;
  const width = Math.max(distance(tl, tr), distance(bl, br));
  const height = Math.max(distance(tl, bl), distance(tr, br));
  const scale = Math.min(1, maxEdge / Math.max(width, height, 1));
  return {
    outW: Math.max(2, Math.round(width * scale)),
    outH: Math.max(2, Math.round(height * scale)),
  };
}

/**
 * Standard document-scan homography via local DLT
 * (same class of math as OpenCV `getPerspectiveTransform`).
 * Dest rectangle → source quad sampling for inverse warp.
 */
export function createQuadHomography(
  sourceCorners: [PixelPoint, PixelPoint, PixelPoint, PixelPoint],
  outW: number,
  outH: number,
): QuadHomography {
  const [tl, tr, br, bl] = sourceCorners;
  const srcPts = [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y];
  const maxX = Math.max(1, outW - 1);
  const maxY = Math.max(1, outH - 1);
  const dstPts = [0, 0, maxX, 0, maxX, maxY, 0, maxY];
  const persp = createPerspectiveTransform(srcPts, dstPts);
  return {
    sampleSource(destX: number, destY: number): PixelPoint {
      const [x, y] = persp.transformInverse(destX, destY);
      return { x, y };
    },
  };
}

/** Sample source pixel with bilinear filtering; out-of-range → transparent skip via alpha 0. */
export function writeBilinearSample(
  data: Uint8ClampedArray,
  dest: Uint8ClampedArray,
  destIndex: number,
  width: number,
  height: number,
  x: number,
  y: number,
) {
  if (x < 0 || y < 0 || x > width - 1 || y > height - 1) {
    dest[destIndex + 3] = 0;
    return;
  }
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(width - 1, x0 + 1);
  const y1 = Math.min(height - 1, y0 + 1);
  const tx = x - x0;
  const ty = y - y0;
  const i00 = (y0 * width + x0) * 4;
  const i10 = (y0 * width + x1) * 4;
  const i01 = (y1 * width + x0) * 4;
  const i11 = (y1 * width + x1) * 4;

  for (let channel = 0; channel < 4; channel++) {
    const top = data[i00 + channel] * (1 - tx) + data[i10 + channel] * tx;
    const bottom = data[i01 + channel] * (1 - tx) + data[i11 + channel] * tx;
    dest[destIndex + channel] = top * (1 - ty) + bottom * ty;
  }
}
